import type { ContentGuidelines, DurationRange, SocialChannel } from './content';

export type MarketplaceDeliverableType =
  | 'ugc_video'
  | 'ugc_photo'
  | 'ugc_photo_carousel'
  | 'instagram_reel'
  | 'instagram_story'
  | 'instagram_post'
  | 'instagram_carousel'
  | 'tiktok'
  | 'tiktok_photo_carousel';

export interface MarketplaceOrderDeliverable {
  id: string;
  type: MarketplaceDeliverableType;
  /** Only for carousel types (ugc_photo_carousel, instagram_carousel, tiktok_photo_carousel). */
  count?: number;
  /** Brand opt-in: require creator to submit draft for review before posting live. Social types only. */
  requiresDraftApproval?: boolean;
  guidelines?: ContentGuidelines;
  /** Video types only. */
  duration?: DurationRange;
  additionalInformation?: string;
  contentBriefFile?: string;
  inspirationLinks?: string[];
}

const SOCIAL_TYPES = new Set<MarketplaceDeliverableType>([
  'instagram_reel',
  'instagram_story',
  'instagram_post',
  'instagram_carousel',
  'tiktok',
  'tiktok_photo_carousel',
]);

export function deliverableRequiresPublishedPost(type: MarketplaceDeliverableType): boolean {
  return SOCIAL_TYPES.has(type);
}

export function deliverableIsCarousel(type: MarketplaceDeliverableType): boolean {
  return (
    type === 'ugc_photo_carousel' ||
    type === 'instagram_carousel' ||
    type === 'tiktok_photo_carousel'
  );
}

const DELIVERABLE_LABELS: Record<MarketplaceDeliverableType, string> = {
  ugc_video: 'UGC Video',
  ugc_photo: 'UGC Photo',
  ugc_photo_carousel: 'UGC Photo Carousel',
  instagram_reel: 'Instagram Reel',
  instagram_story: 'Instagram Story',
  instagram_post: 'Instagram Post',
  instagram_carousel: 'Instagram Carousel',
  tiktok: 'TikTok',
  tiktok_photo_carousel: 'TikTok Photo Carousel',
};

export function deliverableLabel(type: MarketplaceDeliverableType): string {
  return DELIVERABLE_LABELS[type] ?? type;
}

export const MARKETPLACE_DELIVERABLE_TYPE_ORDER: readonly MarketplaceDeliverableType[] = [
  'ugc_video',
  'ugc_photo',
  'ugc_photo_carousel',
  'instagram_reel',
  'instagram_story',
  'instagram_post',
  'instagram_carousel',
  'tiktok',
  'tiktok_photo_carousel',
] as const;

export function deliverableChannel(type: MarketplaceDeliverableType): SocialChannel | undefined {
  switch (type) {
    case 'instagram_reel':
      return 'instagramReels';
    case 'instagram_story':
      return 'instagramStories';
    case 'instagram_post':
    case 'instagram_carousel':
      return 'instagramPosts';
    case 'tiktok':
    case 'tiktok_photo_carousel':
      return 'tiktoks';
    default:
      return undefined;
  }
}

